import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { companyAuth, companySettings, type NotificationLogItem } from '../../api/company/client'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2, Building2, Users, Bell, FileText, Percent, ChevronDown, ChevronRight, Send, Key, Trash2, Landmark, CheckCircle2, AlertCircle } from 'lucide-react'

const PROFILE_LOCALES = ['bhs', 'hr', 'sr', 'en'] as const
const profileSchema = z.object({
  name: z.string().min(1).optional(),
  address: z.string().optional(),
  locale: z.enum(['bhs', 'hr', 'sr', 'en']).optional(),
})

const notificationSchema = z.object({
  notification_email: z.string().optional(),
  notify_on_payment_email: z.boolean(),
  webhook_url: z.string().optional(),
  webhook_secret: z.string().optional(),
  notify_on_payment_webhook: z.boolean(),
}).refine(
  (d) => !d.notify_on_payment_webhook || (d.webhook_url && d.webhook_url.trim().length > 0),
  { message: 'Webhook URL is required when webhook is enabled', path: ['webhook_url'] }
)

type ProfileForm = z.infer<typeof profileSchema>
type NotificationForm = z.infer<typeof notificationSchema>

function formatFeeDisplay(
  percent: number | null | undefined,
  fixed: number | null | undefined,
  kind: 'bank' | 'platform'
): string {
  const hasPercent = percent != null && Number(percent) !== 0
  const hasFixed = fixed != null && Number(fixed) !== 0
  if (!hasPercent && !hasFixed) return '—'
  const parts: string[] = []
  if (hasPercent) parts.push(`${Number(percent).toFixed(2)}%`)
  if (hasFixed) {
    const suffix = kind === 'platform' && !hasPercent ? ' per transaction' : ''
    parts.push(`${Number(fixed).toFixed(2)} BAM${suffix}`)
  }
  return parts.join(' + ')
}

export default function CompanySettings() {
  const { t } = useTranslation('portal')
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'profile' | 'users' | 'notifications' | 'fees' | 'bankAccount' | 'documents' | 'apiKeys'>('profile')
  const [apiKeyModal, setApiKeyModal] = useState<{ open: boolean; key?: string; name?: string | null }>({ open: false })
  const [createKeyFormOpen, setCreateKeyFormOpen] = useState(false)
  const [createKeyForm, setCreateKeyForm] = useState<{ name: string; expires_at: string }>({ name: '', expires_at: '' })

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['company', 'profile'],
    queryFn: () => companyAuth.me().then((r) => r.data),
  })

  const updateProfile = useMutation({
    mutationFn: (data: ProfileForm) => companyAuth.updateMe(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company', 'profile'] }),
  })

  const { data: feeData } = useQuery({
    queryKey: ['company', 'settings', 'fees'],
    queryFn: () => companySettings.feeStructure().then((r) => r.data),
  })
  const { data: feeModeData } = useQuery({
    queryKey: ['company', 'settings', 'fee-mode'],
    queryFn: () => companySettings.feeMode().then((r) => r.data),
  })
  const updateFeeMode = useMutation({
    mutationFn: (fee_mode: 'company_absorbs' | 'customer_pays') => companySettings.updateFeeMode(fee_mode),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company', 'settings', 'fee-mode'] }),
  })
  const { data: bankAccountData } = useQuery({
    queryKey: ['company', 'settings', 'bank-account'],
    queryFn: () => companySettings.bankAccount().then((r) => r.data),
  })
  const [bankInput, setBankInput] = useState('')
  const [bankPreviewName, setBankPreviewName] = useState<string | null>(null)
  const [bankPreviewLoading, setBankPreviewLoading] = useState(false)
  const updateBankAccount = useMutation({
    mutationFn: (bank_account_number: string | null) => companySettings.updateBankAccount(bank_account_number),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'settings', 'bank-account'] })
    },
  })
  const { data: usersData } = useQuery({
    queryKey: ['company', 'settings', 'users'],
    queryFn: () => companySettings.users().then((r) => r.data),
  })
  const { data: notifData } = useQuery({
    queryKey: ['company', 'settings', 'notifications'],
    queryFn: () => companySettings.notifications().then((r) => r.data),
  })
  const [logFilter, setLogFilter] = useState<'all' | 'email' | 'webhook'>('all')
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null)
  const [codeExampleLang, setCodeExampleLang] = useState<'python' | 'node' | 'php' | null>(null)
  const [webhookTestResult, setWebhookTestResult] = useState<{
    success: boolean
    status_code: number | null
    response_body: string
    error_message: string | null
  } | null>(null)
  const { data: logsData } = useQuery({
    queryKey: ['company', 'settings', 'notification-logs', logFilter],
    queryFn: () =>
      companySettings.notificationLogs({
        delivery_type: logFilter === 'all' ? undefined : logFilter,
        skip: 0,
        limit: 50,
      }).then((r) => r.data),
  })
  const testWebhook = useMutation({
    mutationFn: () => companySettings.testWebhook().then((r) => r.data),
    onSuccess: (data) => setWebhookTestResult(data),
    onError: (err: { response?: { data?: { detail?: { message?: string } } }; message?: string }) => {
      const msg = err.response?.data?.detail?.message ?? err.message ?? t('common.error')
      setWebhookTestResult({ success: false, status_code: null, response_body: '', error_message: msg })
    },
  })
  const updateNotifications = useMutation({
    mutationFn: (data: NotificationForm) =>
      companySettings.updateNotifications({
        notification_email: data.notification_email || null,
        notify_on_payment_email: data.notify_on_payment_email,
        webhook_url: data.webhook_url || null,
        webhook_secret: data.webhook_secret || null,
        notify_on_payment_webhook: data.notify_on_payment_webhook,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company', 'settings', 'notifications'] }),
  })
  const {
    register: registerNotif,
    handleSubmit: handleNotifSubmit,
    formState: { errors: notifErrors },
    watch: watchNotif,
  } = useForm<NotificationForm>({
    resolver: zodResolver(notificationSchema),
    values: notifData
      ? {
          notification_email: notifData.notification_email ?? '',
          notify_on_payment_email: notifData.notify_on_payment_email,
          webhook_url: notifData.webhook_url ?? '',
          webhook_secret: notifData.webhook_secret ?? '',
          notify_on_payment_webhook: notifData.notify_on_payment_webhook,
        }
      : undefined,
  })
  const notifyWebhookEnabled = watchNotif('notify_on_payment_webhook')
  const { data: docsData } = useQuery({
    queryKey: ['company', 'settings', 'documents'],
    queryFn: () => companySettings.documents().then((r) => r.data),
  })
  const { data: apiKeysData } = useQuery({
    queryKey: ['company', 'settings', 'api-keys'],
    queryFn: () => companySettings.apiKeys.list().then((r) => r.data),
  })
  const createApiKey = useMutation({
    mutationFn: (data: { name?: string | null; expires_at?: string | null }) => companySettings.apiKeys.create(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['company', 'settings', 'api-keys'] })
      setCreateKeyFormOpen(false)
      setApiKeyModal({ open: true, key: res.data.key, name: res.data.name ?? undefined })
      setCreateKeyForm({ name: '', expires_at: '' })
    },
  })
  const revokeApiKey = useMutation({
    mutationFn: (keyId: string) => companySettings.apiKeys.revoke(keyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company', 'settings', 'api-keys'] }),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    values: profile
      ? {
          name: profile.name,
          address: profile.address ?? '',
          locale: (PROFILE_LOCALES.includes(profile.locale as (typeof PROFILE_LOCALES)[number])
            ? profile.locale
            : 'bhs') as (typeof PROFILE_LOCALES)[number],
        }
      : undefined,
  })

  const tabs = [
    { id: 'profile' as const, labelKey: 'company.settings.tabProfile', icon: Building2 },
    { id: 'users' as const, labelKey: 'company.settings.tabUsers', icon: Users },
    { id: 'notifications' as const, labelKey: 'company.settings.tabNotifications', icon: Bell },
    { id: 'fees' as const, labelKey: 'company.settings.tabFees', icon: Percent },
    { id: 'bankAccount' as const, labelKey: 'company.settings.tabBankAccount', icon: Landmark },
    { id: 'documents' as const, labelKey: 'company.settings.tabDocuments', icon: FileText },
    { id: 'apiKeys' as const, labelKey: 'company.settings.tabApiKeys', icon: Key },
  ]

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('company.settings.title')}</h1>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-4">
        {tabs.map(({ id, labelKey, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
              activeTab === id
                ? 'bg-blue-50 text-blue-600 font-medium'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <Icon className="w-4 h-4" /> {t(labelKey)}
          </button>
        ))}
      </div>

      <div className="rounded-xl bg-white border border-gray-200 p-6 shadow-sm">
        {activeTab === 'profile' && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('company.settings.companyProfile')}</h2>
            {profileLoading ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin" /> {t('company.settings.loading')}
              </div>
            ) : (
              <form
                onSubmit={handleSubmit((d) => updateProfile.mutate(d))}
                className="space-y-4 max-w-md"
              >
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('company.settings.companyName')}</label>
                  <input
                    {...register('name')}
                    className="w-full rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('company.settings.address')}</label>
                  <textarea
                    {...register('address')}
                    rows={3}
                    className="w-full rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('company.settings.notificationLanguage')}</label>
                  <select
                    {...register('locale')}
                    className="w-full rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900"
                  >
                    <option value="bhs">{t('company.settings.localeBhs')}</option>
                    <option value="hr">{t('company.settings.localeHr')}</option>
                    <option value="sr">{t('company.settings.localeSr')}</option>
                    <option value="en">{t('company.settings.localeEn')}</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500">{t('company.settings.notificationLanguageHint')}</p>
                </div>
                <p className="text-gray-500 text-sm">{t('company.settings.tinJib')}: {profile?.tin ?? '—'}</p>
                <button
                  type="submit"
                  disabled={updateProfile.isPending}
                  className="rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {updateProfile.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('company.settings.saveChanges')}
                </button>
                {updateProfile.isSuccess && (
                  <p className="text-green-600 text-sm">{t('company.settings.saved')}</p>
                )}
              </form>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('company.settings.companyUsers')}</h2>
            <p className="text-gray-500 text-sm mb-4">
              {t('company.settings.usersHint')}
            </p>
            <ul className="space-y-2">
              {(usersData?.users ?? []).map((u) => (
                <li
                  key={u.id}
                  className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0"
                >
                  <span className="text-gray-700">{u.email}</span>
                  <span className="rounded px-2 py-0.5 text-xs bg-gray-100 text-gray-700">
                    {u.role}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('company.settings.notifications')}</h2>
              <p className="text-gray-500 text-sm mb-4">{t('company.settings.notificationsHint')}</p>
              <form
                onSubmit={handleNotifSubmit((d) => updateNotifications.mutate(d))}
                className="space-y-6 max-w-2xl"
              >
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      {...registerNotif('notify_on_payment_email')}
                      className="rounded border-gray-300"
                    />
                    <span className="font-medium text-gray-900">{t('company.settings.notifyByEmail')}</span>
                  </label>
                  <p className="text-gray-500 text-sm pl-6">
                    {t('company.settings.notifyByEmailHint', { email: notifData?.registered_email ?? '' })}
                  </p>
                  <div className="pl-6">
                    <label className="block text-sm text-gray-600 mb-1">{t('company.settings.notificationEmail')}</label>
                    <input
                      {...registerNotif('notification_email')}
                      placeholder={t('company.settings.notificationEmailPlaceholder')}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore
                    />
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6 space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      {...registerNotif('notify_on_payment_webhook')}
                      className="rounded border-gray-300"
                    />
                    <span className="font-medium text-gray-900">{t('company.settings.notifyByWebhook')}</span>
                  </label>
                  {notifyWebhookEnabled && (
                    <>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">{t('company.settings.webhookUrl')} *</label>
                        <input
                          {...registerNotif('webhook_url')}
                          placeholder={t('company.settings.webhookUrlPlaceholder')}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 font-mono text-sm"
                        />
                        {notifErrors.webhook_url && (
                          <p className="mt-1 text-sm text-red-600">{t('company.settings.webhookUrlRequired')}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">{t('company.settings.webhookSecret')}</label>
                        <input
                          type="password"
                          autoComplete="new-password"
                          data-lpignore="true"
                          data-1p-ignore
                          {...registerNotif('webhook_secret')}
                          placeholder={t('company.settings.webhookSecretPlaceholder')}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setWebhookTestResult(null)
                            testWebhook.mutate()
                          }}
                          disabled={testWebhook.isPending || !watchNotif('webhook_url')?.trim()}
                          className="inline-flex items-center gap-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {testWebhook.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          {t('company.settings.testWebhook')}
                        </button>
                        <span className="text-gray-500 text-sm">{t('company.settings.testWebhookHint')}</span>
                      </div>
                      {webhookTestResult && (
                        <div className={`rounded-lg border p-4 text-sm ${webhookTestResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                          <h4 className="font-medium mb-2">{t('company.settings.testWebhookResult')}</h4>
                          <p className="mb-1">
                            <span className="text-gray-600">{t('company.settings.testWebhookStatus')}:</span>{' '}
                            <span className={webhookTestResult.success ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                              {webhookTestResult.status_code ?? '—'} {webhookTestResult.success ? t('company.settings.testWebhookSuccess') : (webhookTestResult.error_message ?? '')}
                            </span>
                          </p>
                          {webhookTestResult.response_body ? (
                            <p className="mt-2">
                              <span className="text-gray-600 block mb-1">{t('company.settings.testWebhookResponse')}:</span>
                              <pre className="bg-white border border-gray-200 rounded p-2 text-xs overflow-x-auto max-h-32 overflow-y-auto">
                                {webhookTestResult.response_body || '—'}
                              </pre>
                            </p>
                          ) : null}
                        </div>
                      )}
                      <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 space-y-3 text-sm">
                        <h3 className="font-medium text-gray-900">{t('company.settings.webhookInstructionsTitle')}</h3>
                        <p className="text-gray-600">{t('company.settings.webhookInstructionsIntro')}</p>
                        <p className="text-gray-600">{t('company.settings.webhookRetryPolicy')}</p>
                        <h4 className="font-medium text-gray-800 pt-2">{t('company.settings.webhookSignatureTitle')}</h4>
                        <p className="text-gray-600">{t('company.settings.webhookSignatureIntro')}</p>
                        <h4 className="font-medium text-gray-800 pt-2">{t('company.settings.webhookExamplePayload')}</h4>
                        <p className="text-gray-600 text-sm mb-1">{t('company.settings.webhookPayloadFields')}</p>
                        <pre className="bg-white border border-gray-200 rounded p-3 text-xs overflow-x-auto">
{`{
  "event": "payment.completed",
  "payment_id": "uuid",
  "receipt_id": "uuid",
  "amount": "25.00",
  "currency": "BAM",
  "status": "completed",
  "customer_email": "customer@example.com",
  "fiscal_number": "...",
  "iic": "...",
  "company_id": "uuid",
  "company_name": "...",
  "company_identificator": "4200123450007",
  "sdc_date_time": "2026-02-11T12:49:23+01:00",
  "created_at": "2026-02-11T12:00:00"
}`}
                        </pre>
                        <h4 className="font-medium text-gray-800 pt-2">{t('company.settings.webhookCodeExamples')}</h4>
                        <p className="text-gray-600 text-sm mb-2">{t('company.settings.webhookCodeExamplesHint')}</p>
                        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                          {(['python', 'node', 'php'] as const).map((lang) => (
                            <div key={lang} className="border-b border-gray-200 last:border-b-0">
                              <button
                                type="button"
                                onClick={() => setCodeExampleLang((prev) => (prev === lang ? null : lang))}
                                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded-none"
                              >
                                <span className="font-medium">
                                  {lang === 'python' && t('company.settings.webhookPython')}
                                  {lang === 'node' && t('company.settings.webhookNode')}
                                  {lang === 'php' && t('company.settings.webhookPhp')}
                                </span>
                                {codeExampleLang === lang ? (
                                  <ChevronDown className="w-4 h-4 shrink-0 text-gray-500" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 shrink-0 text-gray-500" />
                                )}
                              </button>
                              {codeExampleLang === lang && (
                                <div className="px-3 pb-3 pt-0">
                                  <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto">
                                    {lang === 'python' && `import hmac, hashlib
sig = hmac.new(
  secret.encode(), body, hashlib.sha256
).hexdigest()
assert headers.get("X-Webhook-Signature") == "sha256=" + sig`}
                                    {lang === 'node' && `const crypto = require('crypto');
const sig = 'sha256=' + crypto
  .createHmac('sha256', secret)
  .update(body).digest('hex');
if (req.headers['x-webhook-signature'] !== sig) throw new Error('Invalid');`}
                                    {lang === 'php' && `$sig = 'sha256=' . hash_hmac('sha256', $body, $secret);
if (($h = $_SERVER['HTTP_X_WEBHOOK_SIGNATURE'] ?? '') !== $sig) throw new Exception('Invalid');`}
                                  </pre>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={updateNotifications.isPending}
                    className="rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white font-medium flex items-center gap-2 disabled:opacity-50"
                  >
                    {updateNotifications.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t('company.settings.saveChanges')}
                  </button>
                  {updateNotifications.isSuccess && (
                    <p className="text-green-600 text-sm">{t('company.settings.saved')}</p>
                  )}
                  {updateNotifications.isError && (
                    <p className="text-red-600 text-sm">
                      {(updateNotifications.error as { response?: { data?: { detail?: { error?: string; message?: string } } } })?.response?.data?.detail?.error === 'WEBHOOK_URL_REQUIRED'
                        ? t('company.settings.webhookUrlRequired')
                        : (updateNotifications.error as { response?: { data?: { detail?: { message?: string } } } })?.response?.data?.detail?.message ?? t('common.error')}
                    </p>
                  )}
                </div>
              </form>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('company.settings.notificationLogs')}</h3>
              <p className="text-gray-500 text-sm mb-4">{t('company.settings.notificationLogsHint')}</p>
              <div className="flex gap-2 mb-4">
                {(['all', 'email', 'webhook'] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setLogFilter(f)}
                    className={`rounded-lg px-3 py-1.5 text-sm ${
                      logFilter === f ? 'bg-blue-50 text-blue-600 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {f === 'all' ? t('company.settings.filterAll') : f === 'email' ? t('company.settings.filterEmail') : t('company.settings.filterWebhook')}
                  </button>
                ))}
              </div>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                {!logsData?.items?.length ? (
                  <p className="p-4 text-gray-500 text-sm">{t('company.settings.noLogs')}</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left p-2 w-8" />
                      <th className="text-left p-2">{t('company.settings.logDate')}</th>
                      <th className="text-left p-2">{t('company.settings.logType')}</th>
                      <th className="text-left p-2">{t('company.settings.logPayment')}</th>
                      <th className="text-left p-2">{t('company.settings.logAttempt')}</th>
                      <th className="text-left p-2">{t('company.settings.logSuccess')}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {(logsData?.items ?? []).map((log: NotificationLogItem) => (
                      <React.Fragment key={log.id}>
                        <tr
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="p-2">
                            <button
                              type="button"
                              onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                              className="text-gray-500"
                            >
                              {expandedLogId === log.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                          </td>
                          <td className="p-2 text-gray-700">
                            {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                          </td>
                          <td className="p-2">{log.delivery_type}</td>
                          <td className="p-2 font-mono text-xs">{log.payment_id.slice(0, 8)}…</td>
                          <td className="p-2">{log.attempt_number}</td>
                          <td className="p-2">
                            <span className={log.success ? 'text-green-600' : 'text-red-600'}>
                              {log.success ? t('company.settings.on') : t('company.settings.off')}
                            </span>
                            {log.error_message && (
                              <span className="text-red-500 text-xs block">{log.error_message}</span>
                            )}
                          </td>
                        </tr>
                        {expandedLogId === log.id && (
                          <tr key={`${log.id}-detail`} className="bg-gray-50">
                            <td colSpan={6} className="p-4 space-y-3">
                              <div>
                                <span className="text-sm font-medium text-gray-700">{t('company.settings.logRequest')}</span>
                                <pre className="mt-1 p-2 bg-white border rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                  {JSON.stringify(log.request_payload ?? log.request_headers ?? {}, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-700">{t('company.settings.logResponse')}</span>
                                {log.response_status != null && (
                                  <span className="ml-2 text-gray-600">HTTP {log.response_status}</span>
                                )}
                                <pre className="mt-1 p-2 bg-white border rounded text-xs overflow-x-auto max-h-40 overflow-y-auto whitespace-pre-wrap">
                                  {log.response_body ?? log.error_message ?? '—'}
                                </pre>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'fees' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('company.settings.feeStructure')}</h2>
              <p className="text-gray-500 text-sm mb-4">
                {t('company.settings.feeStructureHint')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500 text-sm">{t('company.settings.bankFee')}</p>
                  <p className="text-gray-900">
                    {formatFeeDisplay(
                      feeData?.bank_fee_percent ?? (feeData?.bank_fee_type === 'percent' ? feeData?.bank_fee_value : null),
                      feeData?.bank_fee_fixed ?? (feeData?.bank_fee_type === 'fixed' ? feeData?.bank_fee_value : null),
                      'bank'
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">{t('company.settings.platformFee')}</p>
                  <p className="text-gray-900">
                    {formatFeeDisplay(feeData?.platform_fee_percent ?? null, feeData?.platform_fee_fixed ?? null, 'platform')}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('company.settings.feeModeTitle')}</h3>
              <p className="text-gray-500 text-sm mb-4">{t('company.settings.feeModeHint')}</p>
              <div className="space-y-3 max-w-lg">
                <label
                  className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                    (feeModeData?.fee_mode ?? 'company_absorbs') === 'company_absorbs'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="fee_mode"
                    checked={(feeModeData?.fee_mode ?? 'company_absorbs') === 'company_absorbs'}
                    onChange={() => updateFeeMode.mutate('company_absorbs')}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="font-medium text-gray-900">{t('company.settings.feeModeCompanyAbsorbs')}</span>
                    <p className="text-sm text-gray-500 mt-1">{t('company.settings.feeModeCompanyAbsorbsHint')}</p>
                  </div>
                </label>
                <label
                  className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                    feeModeData?.fee_mode === 'customer_pays'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="fee_mode"
                    checked={feeModeData?.fee_mode === 'customer_pays'}
                    onChange={() => updateFeeMode.mutate('customer_pays')}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="font-medium text-gray-900">{t('company.settings.feeModeCustomerPays')}</span>
                    <p className="text-sm text-gray-500 mt-1">{t('company.settings.feeModeCustomerPaysHint')}</p>
                  </div>
                </label>
              </div>
              {updateFeeMode.isPending && (
                <p className="text-gray-500 text-sm mt-2 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t('company.settings.saving')}
                </p>
              )}
              {updateFeeMode.isSuccess && (
                <p className="text-green-600 text-sm mt-2">{t('company.settings.saved')}</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'bankAccount' && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-2">{t('company.settings.bankAccountTitle')}</h2>
            <p className="text-gray-500 text-sm mb-6">{t('company.settings.bankAccountHint')}</p>

            {bankAccountData?.bank_account_number && (
              <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-600 mb-1">{t('company.settings.bankAccountCurrent')}</p>
                <p className="text-lg font-mono text-gray-900 tracking-wider">
                  {bankAccountData.bank_account_number.replace(/(\d{3})(\d{3})(\d{8})(\d{2})/, '$1-$2-$3-$4')}
                </p>
                {bankAccountData.bank_name && (
                  <div className="flex items-center gap-2 mt-2">
                    <Landmark className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">{bankAccountData.bank_name}</span>
                  </div>
                )}
                {!bankAccountData.bank_name && (
                  <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {t('company.settings.bankNotIdentified')}
                  </p>
                )}
              </div>
            )}

            <div className="max-w-md space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('company.settings.bankAccountLabel')}</label>
                <input
                  type="text"
                  maxLength={19}
                  value={bankInput}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^\d]/g, '').slice(0, 16)
                    setBankInput(raw)
                    setBankPreviewName(null)
                    if (raw.length === 16) {
                      setBankPreviewLoading(true)
                      companySettings.identifyBank(raw)
                        .then((r) => setBankPreviewName(r.data.bank_name))
                        .catch(() => setBankPreviewName(null))
                        .finally(() => setBankPreviewLoading(false))
                    }
                  }}
                  placeholder={t('company.settings.bankAccountPlaceholder')}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 font-mono tracking-wider"
                />
                <p className="mt-1 text-sm text-gray-500">{t('company.settings.bankAccountFormat')}</p>
              </div>

              {bankInput.length === 16 && !bankPreviewLoading && (
                <div className={`flex items-center gap-2 p-3 rounded-lg border ${bankPreviewName ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  {bankPreviewName ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-green-800">{t('company.settings.bankIdentified')}</p>
                        <p className="text-sm text-green-700">{bankPreviewName}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                      <p className="text-sm text-amber-800">{t('company.settings.bankNotIdentified')}</p>
                    </>
                  )}
                </div>
              )}
              {bankPreviewLoading && (
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t('company.settings.bankIdentifying')}
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    updateBankAccount.mutate(bankInput.length === 16 ? bankInput : null)
                  }}
                  disabled={updateBankAccount.isPending || (bankInput.length > 0 && bankInput.length !== 16)}
                  className="rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {updateBankAccount.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('company.settings.saveChanges')}
                </button>
                {bankAccountData?.bank_account_number && (
                  <button
                    type="button"
                    onClick={() => {
                      setBankInput('')
                      setBankPreviewName(null)
                      updateBankAccount.mutate(null)
                    }}
                    disabled={updateBankAccount.isPending}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('company.settings.bankAccountRemove')}
                  </button>
                )}
              </div>
              {updateBankAccount.isSuccess && (
                <p className="text-green-600 text-sm">{t('company.settings.saved')}</p>
              )}
              {updateBankAccount.isError && (
                <p className="text-red-600 text-sm">
                  {(updateBankAccount.error as { response?: { data?: { detail?: { message?: string } } } })?.response?.data?.detail?.message ?? t('common.error')}
                </p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('company.settings.documents')}</h2>
            <p className="text-gray-500 text-sm mb-4">
              {t('company.settings.documentsHint')}
            </p>
            <ul className="space-y-2">
              {(docsData?.documents ?? []).map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0"
                >
                  <span className="text-gray-700">{d.name}</span>
                  <span className="text-gray-500 text-sm">
                    {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : t('company.settings.notUploaded')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {activeTab === 'apiKeys' && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('company.settings.apiKeysTitle')}</h2>
            <p className="text-gray-500 text-sm mb-4">{t('company.settings.apiKeysHint')}</p>
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                type="button"
                onClick={() => {
                  setCreateKeyForm({ name: '', expires_at: '' })
                  setCreateKeyFormOpen(true)
                }}
                className="rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white font-medium flex items-center gap-2"
              >
                {t('company.settings.apiKeysCreate')}
              </button>
            </div>
            {createKeyFormOpen && (
              <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50 max-w-md space-y-3">
                <h3 className="font-medium text-gray-900">{t('company.settings.apiKeyGenerate')}</h3>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('company.settings.apiKeyName')}</label>
                  <input
                    type="text"
                    value={createKeyForm.name}
                    onChange={(e) => setCreateKeyForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder={t('company.settings.apiKeyNamePlaceholder')}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('company.settings.apiKeyExpiresAt')}</label>
                  <input
                    type="date"
                    value={createKeyForm.expires_at}
                    onChange={(e) => setCreateKeyForm((f) => ({ ...f, expires_at: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      createApiKey.mutate({
                        name: createKeyForm.name.trim() || null,
                        expires_at: createKeyForm.expires_at.trim() || null,
                      })
                    }}
                    disabled={createApiKey.isPending}
                    className="rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white font-medium flex items-center gap-2 disabled:opacity-50"
                  >
                    {createApiKey.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t('company.settings.apiKeyGenerate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreateKeyFormOpen(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              {!apiKeysData?.items?.length ? (
                <p className="p-4 text-gray-500 text-sm">{t('company.settings.noApiKeys')}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left p-2">{t('company.settings.apiKeyPrefix')}</th>
                      <th className="text-left p-2">Name</th>
                      <th className="text-left p-2">Created</th>
                      <th className="text-left p-2">Expires</th>
                      <th className="text-left p-2 w-20" />
                    </tr>
                  </thead>
                  <tbody>
                    {(apiKeysData?.items ?? []).map((k) => (
                      <tr key={k.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-2 font-mono text-gray-800">{k.key_prefix}…</td>
                        <td className="p-2 text-gray-700">{k.name ?? '—'}</td>
                        <td className="p-2 text-gray-600">{k.created_at ? new Date(k.created_at).toLocaleString() : '—'}</td>
                        <td className="p-2 text-gray-600">{k.expires_at ? new Date(k.expires_at).toLocaleDateString() : '—'}</td>
                        <td className="p-2">
                          <button
                            type="button"
                            onClick={() => window.confirm(t('company.settings.apiKeyRevokeConfirm')) && revokeApiKey.mutate(k.id)}
                            disabled={revokeApiKey.isPending}
                            className="text-red-600 hover:text-red-700 flex items-center gap-1"
                          >
                            <Trash2 className="w-4 h-4" /> {t('company.settings.apiKeyRevoke')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {apiKeyModal.open && apiKeyModal.key && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('company.settings.apiKeyCreatedTitle')}</h3>
                  <p className="text-amber-700 text-sm mb-4">{t('company.settings.apiKeyCreatedWarning')}</p>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={apiKeyModal.key}
                      className="flex-1 rounded border border-gray-300 px-3 py-2 font-mono text-sm bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(apiKeyModal.key!)
                      }}
                      className="rounded-lg bg-gray-100 hover:bg-gray-200 px-4 py-2 text-sm font-medium"
                    >
                      {t('company.settings.apiKeyCopy')}
                    </button>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setApiKeyModal({ open: false })}
                      className="rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2 text-white font-medium"
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
